import { Button, Center, Group, Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { UserResponse } from '@/api/generated/models';
import { MutationButton } from '@/components/common';

interface SendResetEmailModalProps {
  user: UserResponse | null;
  onClose: () => void;
  onConfirm: (userId: string) => void;
  loading?: boolean;
  isPaused?: boolean;
}

const initials = (firstName?: string, lastName?: string) =>
  `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase();

export const SendResetEmailModal = ({
  user,
  onClose,
  onConfirm,
  loading,
  isPaused
}: SendResetEmailModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      opened={!!user}
      onClose={onClose}
      centered
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.55 }}
      title={t('users.roles.sendResetTitle')}
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)',
          padding: '28px 28px 20px'
        },
        title: { fontSize: 19, fontWeight: 700, color: 'var(--color-text-primary)' },
        body: { padding: '18px 28px 28px' }
      }}
    >
      {user && (
        <Stack gap={18}>
          <Group gap={10} wrap="nowrap">
            <Center
              w={32}
              h={32}
              style={{
                borderRadius: 999,
                backgroundColor: 'var(--color-border-soft)',
                border: '1px solid var(--color-border-default)',
                flexShrink: 0
              }}
            >
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {initials(user.firstName, user.lastName)}
              </Text>
            </Center>
            <Stack gap={0}>
              <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {user.firstName} {user.lastName}
              </Text>
              <Text fz={10.5} style={{ color: 'var(--color-text-muted)' }}>
                {user.email}
              </Text>
            </Stack>
          </Group>

          <Text fz={13.5} lh="20px" style={{ color: 'var(--color-text-secondary)' }}>
            {t('users.roles.sendResetMessage')}
          </Text>

          <Group gap={10} wrap="nowrap">
            <Button variant="outline" fullWidth onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              fullWidth
              isPending={loading}
              isPaused={isPaused}
              onClick={() => user.id && onConfirm(user.id)}
            >
              {t('users.roles.sendResetConfirm')}
            </MutationButton>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};
